import React from 'react';

import { Button, Tooltip, Empty, Spin } from 'antd';
import { CaretRightFilled, CaretLeftFilled } from '@ant-design/icons';
import ChartToolbox from 'components/Charts/ChartToolbox';

import { injectIntl, FormattedMessage } from 'react-intl';
import { GRADE_COLORS, BLACK } from 'shared/constants/colors';
import { percent, round } from 'shared/constants/math';
import Chart from './Chart';

import messages from './messages';

const getPageName = (page, formatMessage) => (messages[page] && formatMessage ? formatMessage(messages[page]) : page);

const getPageUrl = (page, formatMessage) => {
  const url = page.shortUrl || page.url;
  return getPageName(url, formatMessage);
};

const getStats = ({ pageviews, visits, respondents }, formatMessage) => `
  ${pageviews !== undefined ? `${formatMessage(messages.pageviews)}: ${pageviews}<br/>` : ''}
  ${visits !== undefined ? `${formatMessage(messages.visits)}: ${visits}<br/>` : ''}
  ${respondents !== undefined ? `${formatMessage(messages.respondents)}: ${respondents}<br/>` : ''}
`;

const renderTransitions = (transitions, formatMessage) => {
  const total = transitions.reduce((sum, t) => sum + t.pageviews, 0);
  return `<table>
    ${transitions.map(t => `<tr><td>${getPageUrl(t.page, formatMessage)}</td><td>${round(percent(t.pageviews, total))}%</td><td>(${t.pageviews})</td></tr>`).join('')}
  </table>`;
};

const nodeTooltip = (page, incoming, outgoing, formatMessage) => `
    <b>${getPageUrl(page, formatMessage)}</b><br/>
    ${getStats(page, formatMessage)}
    <table>
      <tr>
        ${incoming.length ? `<th>${formatMessage(messages.incoming)}</th>` : ''}
        ${outgoing.length ? `<th>${formatMessage(messages.outgoing)}</th>` : ''}
      </tr>
      <tr>
        ${incoming.length ? `<td>${renderTransitions(incoming, formatMessage)}</td>` : ''}
        ${outgoing.length ? `<td>${renderTransitions(outgoing, formatMessage)}</td>` : ''}
      </tr>
    </table>
  `;

const linkTooltip = (transition, formatMessage) => `
  ${formatMessage(messages.from)}: ${getPageName(transition.source, formatMessage)}<br/>
  ${formatMessage(messages.to)}: ${getPageName(transition.target, formatMessage)}<br/>
  ${getStats(transition, formatMessage)}
`;

const EXIT_COLOR = GRADE_COLORS[1];
const OTHER_COLOR = GRADE_COLORS[2];

const FlowChart = ({ intl: { formatMessage: $t },
  title, height, loading, segment, handlePrev, handleNext,
}) => {
  const links = [];
  const data = [];
  const isEmpty = segment.values.length === 0;

  segment.values.forEach(({ pages, transitions }, i) => {
    let hasContent = false;

    const chartData = pages.map((page) => {
      const name = `${page.url} ${i}`;
      let depth = i * 2;
      const itemStyle = {};

      if (page.url === 'exit') {
        itemStyle.color = EXIT_COLOR;
        depth -= 1;
      } else {
        hasContent = true;
      }

      if (page.url === 'other') {
        itemStyle.color = OTHER_COLOR;
      }

      const incoming = transitions
        .filter(t => t.target === page.url)
        .map(t => ({ page: segment.values[i - 1].pages.find(p => p.url === t.source), pageviews: t.pageviews }));
      const outgoing = i < segment.values.length - 1
        ? segment.values[i + 1].transitions
          .filter(t => t.source === page.url)
          .map(t => ({ page: segment.values[i + 1].pages.find(p => p.url === t.target), pageviews: t.pageviews }))
        : [];

      const position = i === segment.values.length - 1 ? 'left' : 'right';

      return {
        depth,
        name,
        itemStyle,
        label: {
          position,
          formatter: () => getPageUrl(page, $t),
        },
        tooltip: {
          position,
          formatter: ({ data: { transition } }) => (
            transition
              ? linkTooltip(transition, $t)
              : nodeTooltip(page, incoming, outgoing, $t)
          ),
        },
      };
    });

    // Add empty node to get correct chart layout
    if (!hasContent) {
      chartData.push({
        name: `other ${i}`,
        depth: (i * 2),
        label: { show: false },
      });
    }

    data.push(...chartData);
    links.push(...transitions.map(transition => ({
      source: `${transition.source} ${i - 1}`,
      target: `${transition.target} ${i}`,
      value: transition.pageviews,
      transition,
    })));
  });

  const getChartOptions = () => ({
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      formatter: ({ data: { transition } }) => linkTooltip(transition, $t),
    },
    series: [{
      type: 'sankey',
      left: 0,
      right: 0,
      top: 10,
      bottom: 10,
      data,
      links,
      emphasis: {
        focus: 'adjacency',
      },
      draggable: false,
      itemStyle: { borderWidth: 0, color: segment.color || BLACK },
    }],
  });

  if (isEmpty) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<FormattedMessage {...messages.noMatchingData} />}
      />
    );
  }

  return (
    <>
      <Spin spinning={!!loading} size="large" tip={<FormattedMessage {...messages.loadingChartData} />}>
        <Chart
          title={title}
          option={getChartOptions()}
          height={height}
        />
      </Spin>

      <ChartToolbox
        left={(
          <Button.Group>
            <Tooltip title={<FormattedMessage {...messages.prev} />}>
              <Button icon={<CaretLeftFilled />} onClick={handlePrev} disabled={!handlePrev} />
            </Tooltip>
            <Tooltip title={<FormattedMessage {...messages.next} />}>
              <Button icon={<CaretRightFilled />} onClick={handleNext} disabled={!handleNext} />
            </Tooltip>
          </Button.Group>
        )}
      />
    </>
  );
};

export default injectIntl(FlowChart);
